import Vue from 'vue';
import authHttp from './authHttp';
import RefreshToken from './refreshToken';
import router from '@/router';
import config from 'BRAND/config';

const AUTH_HEADER = 'X-Authorization';
const AUTH_HEADER_PREFIX = 'Bearer ';

const KEY_ACCESS_TOKEN = 'at';
const KEY_REFRESH_TOKEN = 'rt';

/**
 * Routes that has no handled by login guard.
 * @type {string[]}
 */
const NO_SECURITY_ROUTES = [
  'Login',
  'Signin',
  'Legal',
  'References',
  'EmailVerification',
  'ResetPasswordMail',
  'ResetPasswordForm',
  'UpdateLicense'
];

const login = (context, credentials) => {
  return new Promise((resolve, reject) => {
    authHttp.login(context, credentials).then(response => {
      const access = response.body.accessToken;
      const refresh = response.body.refreshToken;
      saveTokenInfo({access, refresh});

      resolve();
    }, error => {
      reject(error);
    });
  });
};

const emailVerification = (context, verificationToken) => {
  return new Promise((resolve, reject) => {
    authHttp.emailVerification(context, verificationToken).then(response => {
      resolve(response.body);
    }, error => {
      reject(error);
    });
  });
};

const resetPasswordEmail = (context, username) => {
  return new Promise((resolve, reject) => {
    authHttp.resetPasswordEmail(context, username).then(response => {
      resolve(response.body);
    }, error => {
      reject(error);
    });
  });
};
const signin = (context, user) => {
  return new Promise((resolve, reject) => {
    authHttp.signin(context, user).then(() => {
      resolve();
    }, error => {
      reject(error);
    });
  });
};
const resetPasswordForm = (context, user) => {
  return new Promise((resolve, reject) => {
    authHttp.resetPasswordForm(context, user).then(() => {
      resolve();
    }, error => {
      reject(error);
    });
  });
};

const refresh = (context) => {
  return new Promise((resolve, reject) => {
    authHttp.refresh(context).then(response => {
      const access = response.body.accessToken;
      const refresh = response.body.refreshToken;
      saveTokenInfo({access, refresh});

      resolve();
    }, error => {
      reject(error);
    });
  });
};

const uniqueRefreshToken = (context) => {
  if (!RefreshToken.isRunning()) {
    const promise = new Promise((resolve, reject) => {
      refresh(context).then(() => {
        resolve();
        RefreshToken.finish();
      }, error => {
        reject(error);
        RefreshToken.finish();
      });
    });

    RefreshToken.setPromise(promise);
  }

  return RefreshToken.getPromise();
};

const logout = () => {
  cleanTokenInfo();
  router.push('/login', () => { location.reload(); });
};

const isLogged = () => {
  const info = getTokenInfo();
  return info.access !== null && info.refresh !== null;
};

const getTokenInfo = () => {
  const access = localStorage.getItem(KEY_ACCESS_TOKEN);
  const refresh = localStorage.getItem(KEY_REFRESH_TOKEN);
  return {access, refresh};
};

const saveTokenInfo = ({access, refresh}) => {
  localStorage.setItem(KEY_ACCESS_TOKEN, access);
  localStorage.setItem(KEY_REFRESH_TOKEN, refresh);
};

const cleanTokenInfo = () => {
  localStorage.removeItem(KEY_ACCESS_TOKEN);
  localStorage.removeItem(KEY_REFRESH_TOKEN);
};

const tokenInterceptor = (request, next) => {
  if (request.headers.get(authHttp.LOGIN_HEADER)) {
    // Nothing
  } else if (request.headers.get(authHttp.REFRESH_HEADER)) {
    request.headers.set(AUTH_HEADER, AUTH_HEADER_PREFIX + localStorage.getItem(KEY_REFRESH_TOKEN));
  } else {
    request.headers.set(AUTH_HEADER, AUTH_HEADER_PREFIX + localStorage.getItem(KEY_ACCESS_TOKEN));
  }
  next();
};

const loginInterceptor = (request, next) => {
  const context = new Vue();
  next((response) => {
    if (isLogged() && response.status === 401) {
      if (response.body && response.body.message === 'Expired') {
        // Expired
        return Promise.resolve(uniqueRefreshToken(context).then(() => Vue.http(request)));
      } else {
        // Unauthorized
        logout();
      }
    }
  });
};

const samlGuard = (to, from, next) => {
  if (to.path === '/saml') {
    authHttp.loginSaml(new Vue())
      .then(response => {
        const access = response.body.accessToken;
        const refresh = response.body.refreshToken;
        saveTokenInfo({access, refresh});

        document.location.href = '/';
      }).catch(() => next({name: 'Login'}));
  } else {
    next();
  }
};

const loginGuard = (to, from, next) => {
  if (NO_SECURITY_ROUTES.includes(to.name) || isLogged()) {
    next();
  } else {
    next({path: '/login', query: {path: to.fullPath}});
  }
};

const rootGuard = (to, from, next) => {
  if (to.name === 'Root') {
    const redirect = config.home;
    next(redirect);
  } else {
    next();
  }
};

export default {
  // api
  login,
  logout,
  signin,
  emailVerification,
  resetPasswordEmail,
  resetPasswordForm,
  isLogged,
  // interceptors
  tokenInterceptor,
  loginInterceptor,
  // guards
  samlGuard,
  loginGuard,
  rootGuard
};



// WEBPACK FOOTER //
// ./src/api/auth/index.js